import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LegalAlertsPanel = () => {
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  const legalAlerts = [
    {
      id: 'alert-1',
      title: 'Minimum Wage Increase - Effective March 2024',
      description: 'The National Minimum Wage Board has announced an increase in the minimum wage from PGK 3.50 to PGK 4.00 per hour, effective March 1, 2024. All casual and hourly contracts must be updated to reflect this change.',
      severity: 'high',
      category: 'Wage Regulation',
      effectiveDate: '2024-03-01',
      impactedContracts: 45,
      source: 'National Minimum Wage Board Determination 2024',
      actionRequired: 'Update all hourly rate contracts to meet new minimum wage',
      status: 'pending',
      publishedDate: '2024-01-15'
    },
    {
      id: 'alert-2',
      title: 'NASFUND Contribution Rate Adjustment',
      description: 'Proposed amendment to increase employer superannuation contribution from 8.4% to 9.0% of gross salary. Public consultation period ends February 28, 2024.',
      severity: 'medium',
      category: 'Superannuation',
      effectiveDate: '2024-07-01',
      impactedContracts: 230,
      source: 'Superannuation (General Provisions) Amendment Bill 2024',
      actionRequired: 'Monitor legislative progress and prepare contract amendments',
      status: 'monitoring',
      publishedDate: '2024-01-20'
    },
    {
      id: 'alert-3',
      title: 'Extended Maternity Leave Provisions',
      description: 'Employment Act amendment extends paid maternity leave from 6 weeks to 12 weeks for all female employees. Applies to pregnancies with due dates after April 1, 2024.',
      severity: 'high',
      category: 'Leave Entitlements',
      effectiveDate: '2024-04-01',
      impactedContracts: 180,
      source: 'Employment (Amendment) Act 2024',
      actionRequired: 'Update maternity leave clauses in all employment contracts',
      status: 'pending',
      publishedDate: '2024-02-01'
    },
    {
      id: 'alert-4',
      title: 'Work Permit Processing Fee Increase',
      description: 'Department of Labour and Industrial Relations announces increase in work permit application fees for non-citizen employees from PGK 1,000 to PGK 1,500.',
      severity: 'low',
      category: 'Work Permits',
      effectiveDate: '2024-02-15',
      impactedContracts: 12,
      source: 'DLIR Administrative Notice 2024-02',
      actionRequired: 'Update budget forecasts for non-citizen employment costs',
      status: 'acknowledged',
      publishedDate: '2024-01-25'
    },
    {
      id: 'alert-5',
      title: 'Public Holiday Addition - National Reconciliation Day',
      description: 'New public holiday declared for National Reconciliation Day (August 15). Employers must provide paid leave or 2.5x overtime rate for work performed.',
      severity: 'medium',
      category: 'Public Holidays',
      effectiveDate: '2024-08-15',
      impactedContracts: 230,
      source: 'Public Holidays Act (Amendment) 2024',
      actionRequired: 'Update payroll systems and inform all employees',
      status: 'pending',
      publishedDate: '2024-01-30'
    }
  ];

  const filteredAlerts = selectedSeverity === 'all'
    ? legalAlerts
    : legalAlerts?.filter(alert => alert?.severity === selectedSeverity);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-red-100 text-red-700';
      case 'monitoring': return 'bg-amber-100 text-amber-700';
      case 'acknowledged': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const severityCounts = {
    all: legalAlerts?.length,
    high: legalAlerts?.filter(a => a?.severity === 'high')?.length,
    medium: legalAlerts?.filter(a => a?.severity === 'medium')?.length,
    low: legalAlerts?.filter(a => a?.severity === 'low')?.length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Legal & Regulatory Alerts</h3>
          <p className="text-sm text-gray-600 mt-1">Monitor PNG employment law changes affecting contracts</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
          <Icon name="Bell" className="h-4 w-4" />
          Configure Alerts
        </Button>
      </div>

      {/* Severity Filter */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All Alerts', count: severityCounts?.all },
          { id: 'high', label: 'High Priority', count: severityCounts?.high },
          { id: 'medium', label: 'Medium Priority', count: severityCounts?.medium },
          { id: 'low', label: 'Low Priority', count: severityCounts?.low }
        ]?.map((filter) => (
          <button
            key={filter?.id}
            onClick={() => setSelectedSeverity(filter?.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              selectedSeverity === filter?.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter?.label}
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              selectedSeverity === filter?.id ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {filter?.count}
            </span>
          </button>
        ))}
      </div>

      {/* Alert Cards */}
      <div className="space-y-4">
        {filteredAlerts?.map((alert) => (
          <div key={alert?.id} className={`border-2 rounded-lg p-5 ${getSeverityColor(alert?.severity)}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="AlertTriangle" className="h-5 w-5" />
                  <h4 className="font-semibold text-gray-900">{alert?.title}</h4>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{alert?.description}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(alert?.status)}`}>
                {alert?.status?.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
              <div>
                <p className="text-gray-600 font-medium mb-1">Category</p>
                <p className="text-gray-900">{alert?.category}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium mb-1">Effective Date</p>
                <p className="text-gray-900">{new Date(alert?.effectiveDate)?.toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium mb-1">Impacted Contracts</p>
                <p className="text-gray-900 font-semibold">{alert?.impactedContracts}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium mb-1">Published</p>
                <p className="text-gray-900">{new Date(alert?.publishedDate)?.toLocaleDateString()}</p>
              </div>
            </div>

            <div className="bg-white/50 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1">Action Required:</p>
              <p className="text-sm text-gray-900">{alert?.actionRequired}</p>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-600">
                <span className="font-medium">Source:</span> {alert?.source}
              </p>
              <div className="flex gap-2">
                <Button className="text-gray-700 hover:text-gray-900 text-sm font-medium flex items-center gap-1">
                  <Icon name="FileText" className="h-4 w-4" />
                  View Details
                </Button>
                <Button className="text-blue-700 hover:text-blue-900 text-sm font-medium flex items-center gap-1">
                  <Icon name="CheckCircle" className="h-4 w-4" />
                  Mark Acknowledged
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LegalAlertsPanel;